import { removeItems } from 'actions/CheckoutActions'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import Select from 'components/Common/Form/Input/Select'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ExperienceDetailsModalAboutSection from 'components/Experiences/ExperienceDetailsModal/About/ExpereinceDetailsModalAboutSection'
import type { ExperienceDateSelectDateChangeHandler } from 'components/Experiences/ExperienceTickets/ExperienceDateSelect'
import ExperienceDateSelect from 'components/Experiences/ExperienceTickets/ExperienceDateSelect'
import type { ExperienceTicketItemCountChangeHandler } from 'components/Experiences/ExperienceTickets/ExperienceTicketItem'
import ExperienceTicketItem from 'components/Experiences/ExperienceTickets/ExperienceTicketItem'
import ExperienceTicketModeItemSelect from 'components/Experiences/ExperienceTickets/ExperienceTicketModeItemSelect'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { EXP_CATEGORY_AIRPORT_LOUNGE_ID, EXPERIENCE_ANY_DATE } from 'constants/experience'
import useExperienceAvailableDates from 'hooks/ExperiencePage/useExperienceAvailableDates'
import useExperienceAvailableTimes from 'hooks/ExperiencePage/useExperienceAvailableTimes'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useCollinsonAutoTicketMode from 'hooks/ExperiencePage/useCollinsonAutoTicketMode'
import { arrayToObject, EmptyArray, fillArray, groupBy, sum } from 'lib/array/arrayUtils'
import { generateLoyaltyLoungePassItem } from 'lib/checkout/experiences/cart'
import { isLoyaltyLoungePassItem } from 'lib/checkout/checkoutUtils'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { hasMissingRequiredExperienceTickets, mapExperienceToBookingView } from 'lib/experiences/experienceUtils'
import { objectEntries } from 'lib/object/objectUtils'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { generateLoyaltyPointsCalculatorExperienceOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LoyaltyTierBenefitAvailabilityMessageBanner from 'loyalty/components/LoyaltyTierBenefitAvailabilityMessageBanner'
import useLoyaltyUserBenefitView from 'loyalty/hooks/useLoyaltyUserBenefitView'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import {
  cartCountsTowardsBookingLoungeCap,
  countActiveFreeLoungePassRedemptions,
  getFreeLoungePassesRemainingOnBooking,
  getLoungePassBenefitAvailability,
} from 'loyalty/lib/infiniteFreeLoungePassLimit'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import Divider from 'LuxKit/Divider'
import LineClockFiveIcon from 'LuxKit/Icons/line/LineClockFiveIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalImageThumbnail from 'LuxKit/Modal/ModalImageThumbnail'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Heading from 'LuxKit/Typography/Heading'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import { rem } from 'polished'
import { useCallback, useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

/**
 * book later currently not working in back end, so we've disabled it
 */
const bnblEnabled = false

const ExperienceImage = styled(ModalImageThumbnail)`
  height: ${rem(167)};

  ${mediaQueryUp.tablet} {
    height: ${rem(280)};
  }
`

const mainImageParams: ImageParams = { height: 280, fit: 'center' }

function getDefaultTicketModes(ticketModes: Array<App.TicketModeField>): Record<string, string> {
  let result: Record<string, string> = {}
  ticketModes.forEach(mode => {
    if (mode.default) {
      result[mode.key] = mode.default
      mode.options.forEach(option => {
        if (option.key === mode.default && option.fields) {
          result = { ...result, ...getDefaultTicketModes(option.fields) }
        }
      })
    }
  })
  return result
}

export interface FlightDateInfo {
  departingAirport?: string
  arrivalAirport?: string
  departureDate?: string
  arrivalDate?: string
}

interface Props {
  experience: App.ExperienceOffer
  onBack?: () => void
  loungePassViews?: Array<App.Checkout.LoyaltyLoungePassItemView>
  onAddItems: (items: Array<App.Checkout.LoyaltyLoungePassItem>) => void
  afterRemoveItems?: () => void
  checkInDate?: string
  checkOutDate?: string
  flightDates?: Array<FlightDateInfo>
  /**
   * Order this redemption belongs to. Set for post-purchase add-ons so free
   * passes already redeemed on the booking count towards the Infinite cap.
   */
  bookingOrder?: App.Order
}

function LoyaltyLoungePassModalBooking(props: Props) {
  const {
    experience,
    onBack,
    loungePassViews = EmptyArray,
    onAddItems,
    afterRemoveItems,
    checkInDate,
    checkOutDate,
    flightDates,
    bookingOrder,
  }: Props = props
  const intl = useIntl()
  const dispatch = useAppDispatch()

  const loungePassOptionsHeading = intl.formatMessage({
    id: 'iRt4PGgJjMPyo7UigfUe',
    defaultMessage: 'Select your free lounge passes',
    description: 'Section heading for selecting complimentary lounge passes in loyalty booking modal',
  })
  const selectDateAndTimeText = intl.formatMessage({
    id: 'cCKLDFzeqcpJBqttY716',
    defaultMessage: 'Select your date and time to add lounge pass options.',
    description: 'Instructions for selecting date and time for lounge passes',
  })
  const findingDatesText = intl.formatMessage({
    id: '6K55ob8ZFiHmO0DW4pXy',
    defaultMessage: 'Finding available dates...',
    description: 'Loading message while fetching available dates in a modal dialog',
  })
  const findingTimesText = intl.formatMessage({
    id: 'V3R33CM1nDEYOZHotHWL',
    defaultMessage: 'Finding available times...',
    description: 'Loading message while fetching available times in a modal dialog',
  })
  const timeLabel = intl.formatMessage({
    id: 'P5cHF-LFcuVFjFvGLl_3',
    defaultMessage: 'Time',
    description: 'Form field label for time selection dropdown in a modal dialog',
  })
  const selectTimePlaceholder = intl.formatMessage({
    id: 'A0qUdWP5aBpWsZ6wyrmZ',
    defaultMessage: 'Select time',
    description: 'Input placeholder text for time selection dropdown',
  })
  const pleaseSelectTimeError = intl.formatMessage({
    id: 'gtgSQ_q1XQ1gpO7Xf3nG',
    defaultMessage: 'Please select a time',
    description: 'Validation error when time is not selected when an issue occurs',
  })
  const bookDateAndTimeLaterText = intl.formatMessage({
    id: 'kJlZljRS4H5AVpmfdV8Z',
    defaultMessage: 'Book date and time later',
    description: 'Checkbox label to book lounge pass date and time later',
  })
  const selectDateAndTimeLaterText = useMemo(() => {
    const purchaseSynonym = config.brandConfig.purchaseSynonym
    if (purchaseSynonym === 'escape') {
      return intl.formatMessage({
        id: 'DTw7UyY2Cto8kVs59WKr',
        defaultMessage: 'You can select the date and time for your passes later via My Escapes',
        description: 'Message explaining user can select pass date and time later in My Escapes on escape-branded sites',
      })
    }
    if (purchaseSynonym === 'booking') {
      return intl.formatMessage({
        id: '2ku26JeIBTj9AosjDUlg',
        defaultMessage: 'You can select the date and time for your passes later via My Bookings',
        description: 'Message explaining user can select pass date and time later in My Bookings on booking-branded sites',
      })
    }
    return intl.formatMessage({
      id: '3aaowpzFRsYUUT3cF5B6',
      defaultMessage: 'You can select the date and time for your passes later via My Purchases',
      description: 'Message explaining user can select pass date and time later in My Purchases on purchase-branded sites',
    })
  }, [intl])
  const selectValidTicketError = intl.formatMessage({
    id: 'QlNsqKNHrzykEyLAWhUY',
    defaultMessage: 'Please select at least one valid ticket',
    description: 'Form validation error when no valid ticket is selected',
  })
  const validForText = intl.formatMessage({
    id: 'EmENifCHWIuwd5PYzMli',
    defaultMessage: 'Valid for 12 months from the selected date · Date cannot be changed.',
    description: 'Validity note for Collinson lounge passes after the customer selects a lounge access date',
  })
  const allFreePassesAllocatedText = intl.formatMessage({
    id: 'U5ur9FGDr1J1BoQ28nLt',
    defaultMessage: 'All your free lounge passes have already been added to your booking',
    description: 'Info message when every free lounge pass is already redeemed on this booking or sitting in the cart',
  })
  const freePassLimitErrorText = intl.formatMessage({
    id: 'aBltVF-1vVNVt1VmBQPr',
    defaultMessage: 'You have selected more free lounge passes than are available. Reduce the number of free passes to continue.',
    description: 'Warning in the lounge pass modal when the selected free passes exceed the remaining allowance',
  })
  const needExtraLoungePassesText = intl.formatMessage({
    id: '-fbF8JJBpQfo6SaXynTX',
    defaultMessage: 'Need extra lounge passes? Simply add more below',
    description: 'Heading prompting user to add extra lounge passes in a modal dialog',
  })
  const aboutLoungePassTitle = intl.formatMessage({
    id: 'byp2oIBpYfRRaULf478e',
    defaultMessage: 'About the Lounge Pass',
    description: 'Section title for lounge pass details in a modal dialog',
  })
  const updateButtonText = intl.formatMessage({
    id: 'BjOjPotvtbvXuf1ncB8t',
    defaultMessage: 'Update',
    description: 'Button text to update existing lounge pass booking',
  })
  const addToBookingButtonText = intl.formatMessage({
    id: 'M4X6MD6sCM-F0Oio93-T',
    defaultMessage: 'Add to booking',
    description: 'Button text to add lounge passes to booking in the booking flow',
  })
  const removeButtonText = intl.formatMessage({
    id: 'IbRHzDaI1W07lSEh3pVx',
    defaultMessage: 'Remove',
    description: 'Action button label to remove lounge passes from booking',
  })
  const [bookLater, setBookLater] = useState<boolean>(!!loungePassViews[0]?.item.isBookLater)
  const [date, setDate] = useState<string | undefined>(loungePassViews[0]?.item.date)
  const [time, setTime] = useState<string | undefined>(loungePassViews[0]?.item.time)
  const [freeTickets, setFreeTickets] = useState<Record<string, number>>(() => {
    const freeTicketViews = groupBy(loungePassViews.filter(view => view.item.isFree), view => view.item.ticketId)
    // convert to ticketId -> number of tickets object
    return arrayToObject(freeTicketViews.values(), views => views[0].item.ticketId, views => views.length)
  })
  const [tickets, setTickets] = useState<Record<string, number>>(() => {
    const purchasedTicketViews = groupBy(loungePassViews.filter(view => !view.item.isFree), view => view.item.ticketId)
    // convert to ticketId -> number of tickets object
    return arrayToObject(purchasedTicketViews.values(), views => views[0].item.ticketId, views => views.length)
  })
  const [hasRequiredTicketsError, setHasRequiredTicketsError] = useState(false)
  const [freePassLimitError, setFreePassLimitError] = useState(false)

  const isCollinson = experience.provider === 'cln'
  const collinsonUpsellEnabled = useMemo(() => {
    if (!isCollinson) return false
    const airportCode = experience.id.split(':')[1]
    if (!airportCode) return false
    return isEnabledForFeature(config.COLLINSON_LOUNGE_PASS_UPSELL_AIRPORTS, airportCode)
  }, [isCollinson, experience.id])

  const loungePassBenefit = useLoyaltyUserBenefitView('lounge_pass')
  const isInfiniteTier = useAppSelector(getLoyaltyIsInfiniteAccountTier)

  // Count free lounge passes in the cart that won't be replaced on submit.
  // loungePassViews contains items that will be removed on submit, so exclude them.
  const cartItems = useAppSelector(state => state.checkout.cart.items)
  const cartExistingOrderId = useAppSelector(state => state.checkout.cart.existingOrder?.id)
  const cartFreeLoungePassCount = useMemo(() => {
    const replacedItemIds = new Set(loungePassViews.map(view => view.item.itemId))
    return cartItems.filter(item =>
      isLoyaltyLoungePassItem(item) && item.isFree && !replacedItemIds.has(item.itemId),
    ).length
  }, [cartItems, loungePassViews])
  const freePassesAlreadyInCart = cartCountsTowardsBookingLoungeCap(bookingOrder?.id, cartExistingOrderId)
    ? cartFreeLoungePassCount
    : 0
  const redeemedFreeLoungePasses = countActiveFreeLoungePassRedemptions(
    bookingOrder?.loyaltyDetails.benefitsRedeemed,
    bookingOrder?.experienceItems,
  )
  const loungePassBenefitAvailability = getLoungePassBenefitAvailability(isInfiniteTier, loungePassBenefit?.availability)

  const { resolve } = useModalElementContext()
  const isAirportLounge = experience.categories.some(cat => cat.id === EXP_CATEGORY_AIRPORT_LOUNGE_ID)
  const whatToRememberContent = useMemo(() => {
    if (!isAirportLounge) return undefined
    return experience.copy.thingsToKnow.find(item => item.title.toLowerCase().includes('remember'))?.content
  }, [isAirportLounge, experience.copy.thingsToKnow])

  const experienceView = useMemo(() => {
    const view = mapExperienceToBookingView(experience)
    if (!isCollinson) return view
    return {
      ...view,
      originalExperience: {
        ...view.originalExperience,
        copy: {
          ...view.originalExperience.copy,
          thingsToKnow: view.originalExperience.copy.thingsToKnow.filter(item => !item.title.toLowerCase().includes('remember')),
        },
      },
    }
  }, [isCollinson, experience])

  // lounge pass experiences will only ever have 1, they are for a specific airport
  const defaultLocation = experience.redemptionLocations[0].id
  const hasCalendar = experience.hasCalendar
  const hasTimeslots = experience.hasTimeslots

  const collinsonAutoTicketMode = useCollinsonAutoTicketMode({
    provider: experience.provider,
  })

  // Ticket mode selection state, initialised with defaults matching ExperienceTicketInputs
  const [ticketModesMap, setTicketModesMap] = useState<Record<string, string>>(() => {
    const defaults = getDefaultTicketModes(experience.ticketModes ?? [])

    if (collinsonAutoTicketMode) {
      const domesticIntlField = experience.ticketModes?.find(field =>
        field.options.some(opt => opt.key === 'DOMESTIC') &&
        field.options.some(opt => opt.key === 'INTERNATIONAL'),
      )

      if (domesticIntlField) {
        defaults[domesticIntlField.key] = collinsonAutoTicketMode
        const selectedOption = domesticIntlField.options.find(opt => opt.key === collinsonAutoTicketMode)

        if (selectedOption?.fields) {
          Object.assign(defaults, getDefaultTicketModes(selectedOption.fields))
        }
      }
    }
    return defaults
  })

  // Sync ticketModesMap when flight data resolves after initial mount
  useEffect(() => {
    if (!collinsonAutoTicketMode) return
    const ticketModes = experience.ticketModes ?? []
    const domesticIntlField = ticketModes.find(field =>
      field.options.some(opt => opt.key === 'DOMESTIC') &&
      field.options.some(opt => opt.key === 'INTERNATIONAL'),
    )
    if (!domesticIntlField) return
    setTicketModesMap(prev => {
      if (prev[domesticIntlField.key] === collinsonAutoTicketMode) return prev

      const updated = { ...prev, [domesticIntlField.key]: collinsonAutoTicketMode }
      const selectedOption = domesticIntlField.options.find(opt => opt.key === collinsonAutoTicketMode)

      if (selectedOption?.fields) {
        Object.assign(updated, getDefaultTicketModes(selectedOption.fields))
      }

      setFreeTickets({})
      setTickets({})
      return updated
    })
  }, [collinsonAutoTicketMode, experience.ticketModes])

  const hasTicketModes = (experience.ticketModes?.length ?? 0) > 0
  const shouldShowTicketModes = hasTicketModes && !collinsonAutoTicketMode

  const [dates, fetchingDates, datesError] = useExperienceAvailableDates(experience, {
    isBuyNowBookLater: bookLater,
    redemptionLocationId: defaultLocation,
    ticketModesMap: hasTicketModes ? ticketModesMap : undefined,
  })

  const timesDate = useMemo(() => {
    if (bookLater) return undefined
    if (!hasCalendar) return EXPERIENCE_ANY_DATE
    return date
  }, [bookLater, hasCalendar, date])

  const [timeslots, fetchingTimeslots, timeslotsError] = useExperienceAvailableTimes(experience, {
    date: timesDate,
    isBuyNowBookLater: bookLater,
    redemptionLocationId: defaultLocation,
    ticketModesMap: hasTicketModes ? ticketModesMap : undefined,
  })

  const somethingWentWrongMessage = intl.formatMessage(
    {
      id: 'ACO5WjOPFhjN9Q27ycKx',
      defaultMessage: 'Something went wrong finding {type}',
      description: 'Error notification displayed when dates or times fail to load',
    },
    { type: timeslotsError ? 'times' : 'dates' },
  )

  const autoDateFromFlight = useMemo(() => {
    const airportCode = experience.airport?.code
    if (!hasCalendar || !airportCode || !dates.length || !flightDates?.length) return undefined
    for (const flight of flightDates) {
      if (flight.departingAirport === airportCode && flight.departureDate) {
        if (dates.some(d => d.day === flight.departureDate && !d.soldOut)) return flight.departureDate
      }
      if (flight.arrivalAirport === airportCode && flight.arrivalDate) {
        if (dates.some(d => d.day === flight.arrivalDate && !d.soldOut)) return flight.arrivalDate
      }
    }
    return undefined
  }, [hasCalendar, experience.airport?.code, flightDates, dates])

  useEffect(() => {
    if (dates.length > 0 && !hasCalendar) {
      setDate(dates[0].day)
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [dates])

  useEffect(() => {
    if (autoDateFromFlight && !date) {
      setDate(autoDateFromFlight)
    }
  }, [autoDateFromFlight, date])

  useEffect(() => {
    if (!hasTimeslots && timeslots.length > 0) {
      setTime(timeslots[0].time)
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [timeslots])

  const selectedTimeslot = useMemo(() => {
    if (!hasTimeslots || timeslots.length === 1) {
      return timeslots[0]
    }
    return bookLater ? timeslots[0] : timeslots.find(slot => slot.time === time)
  }, [bookLater, hasTimeslots, timeslots, time])

  const selectedTimeslotTicketCounts = useMemo(() => arrayToObject(
    selectedTimeslot?.tickets ?? [],
    ticket => ticket.id,
    ticket => (freeTickets[ticket.id] ?? 0) + (tickets[ticket.id] ?? 0),
  ), [freeTickets, selectedTimeslot?.tickets, tickets])

  const hasMissingRequiredTickets = useMemo(
    () => hasMissingRequiredExperienceTickets(selectedTimeslot?.tickets ?? [], selectedTimeslotTicketCounts),
    [selectedTimeslot?.tickets, selectedTimeslotTicketCounts],
  )

  // Create a more consumable (array) version of the tickets the user currently has selected
  const ticketViews = useMemo(() => {
    const ticketsById = arrayToObject(selectedTimeslot?.tickets ?? [], ticket => ticket.id)
    const freeItems = objectEntries(freeTickets)
      .flatMap(([ticketId, ticketCount]) => fillArray(ticketCount).map(() => ticketsById[ticketId]))
      .map(ticket => ({
        ticket,
        date,
        time: selectedTimeslot?.time,
        isBookLater: bookLater,
        isFree: true,
      }))

    const paidItems = objectEntries(tickets)
      .flatMap(([ticketId, ticketCount]) => fillArray(ticketCount).map(() => ticketsById[ticketId]))
      .map(ticket => ({
        ticket,
        date,
        time: selectedTimeslot?.time,
        isBookLater: bookLater,
        isFree: false,
      }))

    return freeItems.concat(paidItems)
  }, [bookLater, date, freeTickets, selectedTimeslot?.tickets, selectedTimeslot?.time, tickets])

  const onRemoveItems = useCallback(() => {
    dispatch(removeItems(loungePassViews.map(view => view.item.itemId)))
    afterRemoveItems?.()
    resolve()
  }, [dispatch, loungePassViews, resolve, afterRemoveItems])

  const onSubmit = useCallback<React.FormEventHandler<HTMLFormElement>>(e => {
    e.preventDefault()

    // When editing and all tickets are set to 0, treat as a remove action
    if (loungePassViews.length && ticketViews.length === 0) {
      onRemoveItems()
      return
    }

    if (hasMissingRequiredTickets) {
      setHasRequiredTicketsError(true)
      return
    }

    const freePassesAllowed = getFreeLoungePassesRemainingOnBooking({
      isInfinite: isInfiniteTier,
      benefitAvailability: loungePassBenefitAvailability,
      redeemedOnOrder: redeemedFreeLoungePasses,
      freePassesInCart: freePassesAlreadyInCart,
      accountFreePassesInCart: cartFreeLoungePassCount,
      freePassesSelected: 0,
    })
    const selectedFreePasses = ticketViews.filter(view => view.isFree).length
    if (selectedFreePasses > freePassesAllowed) {
      setFreePassLimitError(true)
      return
    }

    if (loungePassViews.length) {
      // if we have existing items, make sure to clear them first, it's too hard to update in place
      dispatch(removeItems(loungePassViews.map(view => view.item.itemId)))
    }
    const newItems = ticketViews.map(view => generateLoyaltyLoungePassItem(experience, view.ticket, {
      date: view.date,
      isBookLater: view.isBookLater,
      isFree: view.isFree,
      time: view.time,
    }))

    onAddItems(newItems)
    resolve()
  }, [
    dispatch,
    experience,
    freePassesAlreadyInCart,
    cartFreeLoungePassCount,
    isInfiniteTier,
    loungePassBenefitAvailability,
    loungePassViews,
    onAddItems,
    onRemoveItems,
    redeemedFreeLoungePasses,
    resolve,
    ticketViews,
    hasMissingRequiredTickets,
  ])

  const onTimeChange = useCallback<React.ChangeEventHandler<HTMLSelectElement>>(e => {
    setTime(e.currentTarget.value)
  }, [])

  const onDateChange = useCallback<ExperienceDateSelectDateChangeHandler>(experienceDate => {
    setDate(experienceDate.day)
  }, [])

  const onTicketModeChange = useCallback((newData: Record<string, string | undefined>) => {
    const updated: Record<string, string> = {}
    for (const key in newData) {
      if (newData[key]) {
        updated[key] = newData[key]!
      }
    }
    setTicketModesMap(updated)
    setFreeTickets({})
    setTickets({})
  }, [])

  const onFreeTicketCountChange = useCallback<ExperienceTicketItemCountChangeHandler>((ticket, count) => {
    if (hasRequiredTicketsError) setHasRequiredTicketsError(false)
    if (freePassLimitError) setFreePassLimitError(false)
    setFreeTickets(prev => {
      const otherFreePasses = Object.values(prev).reduce((total, ticketCount) => total + ticketCount, 0) - (prev[ticket.id] ?? 0)
      const maxForTicket = getFreeLoungePassesRemainingOnBooking({
        isInfinite: isInfiniteTier,
        benefitAvailability: loungePassBenefitAvailability,
        redeemedOnOrder: redeemedFreeLoungePasses,
        freePassesInCart: freePassesAlreadyInCart,
        accountFreePassesInCart: cartFreeLoungePassCount,
        freePassesSelected: otherFreePasses,
      })
      return { ...prev, [ticket.id]: Math.min(Math.max(count, 0), maxForTicket) }
    })
  }, [
    freePassesAlreadyInCart,
    cartFreeLoungePassCount,
    freePassLimitError,
    hasRequiredTicketsError,
    isInfiniteTier,
    loungePassBenefitAvailability,
    redeemedFreeLoungePasses,
  ])

  const onTicketCountChange = useCallback<ExperienceTicketItemCountChangeHandler>((ticket, count) => {
    if (hasRequiredTicketsError) setHasRequiredTicketsError(false)
    if (freePassLimitError) setFreePassLimitError(false)
    setTickets(prev => ({ ...prev, [ticket.id]: count }))
  }, [freePassLimitError, hasRequiredTicketsError])

  const onBookLaterChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(e => {
    setBookLater(e.currentTarget.checked)
  }, [])

  const freeTicketCount = useMemo(() => ticketViews.filter(view => view.isFree).length, [ticketViews])
  const freePassesRemaining = getFreeLoungePassesRemainingOnBooking({
    isInfinite: isInfiniteTier,
    benefitAvailability: loungePassBenefitAvailability,
    redeemedOnOrder: redeemedFreeLoungePasses,
    freePassesInCart: freePassesAlreadyInCart,
    accountFreePassesInCart: cartFreeLoungePassCount,
    freePassesSelected: freeTicketCount,
  })
  const total = useMemo(() => sum(ticketViews, view => view.isFree ? 0 : view.ticket.price), [ticketViews])
  const totalPointsEarnCalculationRequest = useMemo(() => generateLoyaltyPointsCalculatorExperienceOptions(experience, {
    price: total,
  }), [experience, total])

  const selectedEligibleTimeslotTickets = useMemo(() => {
    return selectedTimeslot?.tickets.filter(ticket => ticket.isLoyaltyEligible) ?? []
  }, [selectedTimeslot])

  return <form onSubmit={onSubmit}>
    <ModalHeader
      title={experience.name}
      subtitle={experience.vendor?.name}
      onBackButtonClick={onBack}
    />
    <ModalBody>
      <ExperienceImage image={experience.images[0]} imageParams={mainImageParams} />
      <ModalContent>
        <VerticalSpacer gap={32}>
          <LoyaltyTierBenefitAvailabilityMessageBanner benefitType="lounge_pass" benefitAvailability={freePassesRemaining} />
          {freePassesRemaining === 0 && (isInfiniteTier ? freePassesAlreadyInCart > 0 || redeemedFreeLoungePasses > 0 : cartFreeLoungePassCount > 0) && <MessageBanner kind="informational" icon={<LineInfoCircleIcon />}>
            {allFreePassesAllocatedText}
          </MessageBanner>}
          {freePassLimitError && <MessageBanner kind="warning" icon={<LineInfoCircleIcon />}>
            {freePassLimitErrorText}
          </MessageBanner>}
          <Group direction="vertical" gap={20} tabletGap={24}>
            <Group direction="vertical" gap={8}>
              <Heading variant="heading4">{loungePassOptionsHeading}</Heading>
              {isCollinson && <BodyTextBlock variant="medium" startIcon={<LineCheckCircleIcon colour="success" size="S" />}>
                {validForText}
              </BodyTextBlock>}
            </Group>
            {hasCalendar && <BodyText variant="small" colour="light-foreground-alt">
              {selectDateAndTimeText}
            </BodyText>}
            <Group direction="vertical" gap={20} style={{ position: 'relative' }}>
              <LoadingIndicator floating visible={fetchingTimeslots || fetchingDates} childrenBelow>
                {hasCalendar && fetchingDates && findingDatesText}
                {hasCalendar && fetchingTimeslots && findingTimesText}
              </LoadingIndicator>
              <VerticalSpacer gap={16}>
                {(timeslotsError || datesError) && <MessageBanner kind="critical" icon={<LineInfoCircleIcon />}>
                  {somethingWentWrongMessage}
                </MessageBanner>}
                {shouldShowTicketModes && experience.ticketModes?.map(field => (
                  <ExperienceTicketModeItemSelect
                    key={field.key}
                    ticketModeField={field}
                    selectedOptions={ticketModesMap}
                    onOptionSelect={onTicketModeChange}
                  />
                ))}
                {!hasCalendar && date && <HiddenInput name="date" value={date} />}
                {hasCalendar && <Group direction="vertical" tabletDirection="horizontal" gap={8} tabletHorizontalAlign="stretch">
                  <ExperienceDateSelect
                    name="date"
                    disabled={bookLater}
                    onDateChange={onDateChange}
                    availableDates={dates}
                    selectedDate={date}
                    checkInDate={checkInDate}
                    checkOutDate={checkOutDate}
                    required
                  />
                  {!hasTimeslots && timeslots.length > 0 && <HiddenInput name="time" value={timeslots[0].time} />}
                  {hasTimeslots && timeslots.length <= 1 && <div />}
                  {hasTimeslots && timeslots.length > 1 && <Select
                    startIcon={<LineClockFiveIcon />}
                    label={timeLabel}
                    name="time"
                    placeholder={selectTimePlaceholder}
                    requiredErrorMessage={pleaseSelectTimeError}
                    required
                    value={time}
                    onChange={onTimeChange}
                    disabled={bookLater || !date}
                  >
                    {timeslots.map(slot => <option key={slot.time} value={slot.time}>
                      {slot.time}
                    </option>)}
                  </Select>}
                </Group>}
                {!hasTimeslots && !hasCalendar && timeslots.length > 0 && <HiddenInput name="time" value={timeslots[0].time} />}
                {bnblEnabled && <CheckboxInput checked={bookLater} onChange={onBookLaterChange}>
                  {bookDateAndTimeLaterText}
                </CheckboxInput>}
              </VerticalSpacer>
              {bookLater && <MessageBanner kind="informational" icon={<LineInfoCircleIcon />}>
                {selectDateAndTimeLaterText}
              </MessageBanner>}
              {selectedTimeslot && <Group direction="vertical" gap={24} style={{ position: 'relative' }}>
                <HiddenInput
                  value={ticketViews.filter(ticket => !ticket.ticket.isExtra).length > 0 ? 'filled' : undefined}
                  required={!loungePassViews.length}
                  invalidMessage={selectValidTicketError}
                />
                {hasRequiredTicketsError && <MessageBanner kind="warning" icon={<LineInfoCircleIcon />}>
                  {intl.formatMessage({
                    id: 'OIH7ZHyMTwggPAdUP7Cn',
                    defaultMessage: 'Please select all required ticket options',
                    description: 'Warning message when required experience tickets are not selected',
                  })}
                </MessageBanner>}
                <VerticalSpacer gap={12}>
                  <div>
                    {selectedEligibleTimeslotTickets.map((ticket, index) => <React.Fragment key={ticket.id}>
                      {index !== 0 && <Divider kind="primary" />}
                      <ExperienceTicketItem
                        key={ticket.id}
                        ticket={ticket}
                        count={freeTickets[ticket.id]}
                        isComplimentary
                        name="freeTicketCount"
                        purchaseLimitRemaining={freePassesRemaining}
                        onTicketCountChange={onFreeTicketCountChange}
                        offer={experience}
                      />
                    </React.Fragment>)}
                  </div>
                </VerticalSpacer>
                {(!isCollinson || collinsonUpsellEnabled) && <VerticalSpacer gap={12}>
                  <BodyText variant="medium" weight="bold">{needExtraLoungePassesText}</BodyText>
                  <div>
                    {selectedTimeslot.tickets.map((ticket, index) => <React.Fragment key={ticket.id}>
                      {index !== 0 && <Divider kind="primary" />}
                      <ExperienceTicketItem
                        key={ticket.id}
                        ticket={ticket}
                        count={tickets[ticket.id]}
                        onTicketCountChange={onTicketCountChange}
                        offer={experience}
                      />
                    </React.Fragment>)}
                  </div>
                </VerticalSpacer>}
              </Group>}
            </Group>
          </Group>
          <Divider kind="primary" />
          <ExperienceDetailsModalAboutSection
            experienceView={experienceView}
            variant="overview-first"
            title={aboutLoungePassTitle}
            locationNotice={whatToRememberContent}
          />
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
    <ModalFooter
      primaryActionProps={{
        children: loungePassViews.length ? updateButtonText : addToBookingButtonText,
        type: 'submit',
      }}
      secondaryActionProps={{
        children: removeButtonText,
        onClick: onRemoveItems,
      }}
      isSecondaryActionHidden={!loungePassViews.length}
    >
      {total > 0 && <Group direction="vertical" fullWidth>
        <PriceRowPrice size="M" price={total} saleUnit="total" />
        <LoyaltyPointsEarnWithBreakdownModal
          size="S"
          calculationRequests={totalPointsEarnCalculationRequest}
          calculationType="exact"
          offer={experience}
          hideStatusCredits
        />
      </Group>}
    </ModalFooter>
  </form>
}

export default LoyaltyLoungePassModalBooking
